import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import cn from 'classnames';
import { useTranslation } from 'react-i18next/';

import { useProtectedRoute } from '@hooks/index';

import { PersonalDataFormValues } from '@components/forms/PersonalDataForm/PersonalDataForm.props';

import { useStores } from '@models/RootStore/RootStore';

import styles from './MyAccountPage.module.scss';
import AddVisitedCountryBlock from '@components/containers/AddVisitedCountryBlock/AddVisitedCountryBlock';
import BucketListBlock from '@components/containers/BucketListBlock/BucketListBlock';
import PersonalDataForm from '@components/forms/PersonalDataForm/PersonalDataForm';
import { Meta } from '@components/layout/Meta/Meta';
import { IMyAccountPublicationFields } from 'modules/MyAccountModule';

interface Props {
  fields: IMyAccountPublicationFields;
}

const MyAccountPage = ({ fields }: Props) => {
  const { t } = useTranslation();
  const { UserStore } = useStores();
  const formRef = useRef<any>();

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setLoading] = useState(false);

  const email = UserStore.profile?.email_address;

  useProtectedRoute();

  useEffect(() => {
    if (email) {
      formRef.current?.setFieldValue('email', email);
    }
  }, [email]);

  const handleSubmit = async ({ password }: PersonalDataFormValues) => {
    setError('');
    try {
      setLoading(true);
      await UserStore.updateProfile(password);
      setMessage(t('DOMAIN.ACCOUNT.UPDATE_PROFILE_SUCCESSFUL'));
    } catch (e) {
      setError(t('DOMAIN.ACCOUNT.UPDATE_PROFILE_FAILED'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Meta
        title={fields.meta_title || fields.title}
        description={fields.meta_description || fields.description}
      />
      <div className={styles.MyAccountPage} data-testid="MyAccountPage">
        <div className="container">
          <div className="row">
            <div className="col-md-7">
              <h1 className="h1--large mb-sm">
                {t('DOMAIN.ACCOUNT.WELCOME_USER', { name: UserStore.profile?.first_name })}
              </h1>
              <p className="h3">{fields.description}</p>

              <PersonalDataForm
                onSubmit={handleSubmit}
                ref={formRef}
                deleteAccountText={fields.delete_account_text}
                isLoading={isLoading}
                error={error}
                message={message}
                initialValues={{
                  email: UserStore.profile?.email_address || '',
                  password: '',
                }}
              />
            </div>
          </div>
          <div className={cn('row', styles.Visited)}>
            <div className="col-md-6 mb-md">
              <AddVisitedCountryBlock />
            </div>
            <div className="col-md-6">
              <BucketListBlock items={UserStore.bucketList} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default observer(MyAccountPage);
